import uuid from 'uuid';
import forEach from 'lodash-es/forEach';
import isFunction from 'lodash-es/isFunction';
import noop from 'lodash-es/noop';
import omit from 'lodash-es/omit';
import pick from 'lodash-es/pick';
import remove from 'lodash-es/remove';
import set from 'lodash-es/set';
import throttle from 'lodash-es/throttle';

import utils from '../../common/utils';
import variables from '../../common/variables';
import { migrateJSON } from '../../common/migrations';
import * as viewport from '../../common/viewport';
import fabric from '../../common/fabric';
import * as symbolActions from './symbolActions';
import { overrideImage } from './imagesActions';
import { generateSlideThumbnail } from './thumbnailActions';
import { uploadFile } from '../../common/actions/uploadActions';
import {
  getTemplateById,
  getCommonThemeForSlides,
  getBlankTemplateByThemeId,
  fixObjectsReferencesFromObjectTree,
} from '../../common/lib/themes';
import { MODE_SLIDE, MODE_SYMBOL, MODE_TEMPLATE } from '../../common/lib/modes';

import {
  createObject,
  configureObject,
  isObjectOfType,
  isActiveSelection,
  identifyObject,
  identifyObjectTree,
  ObjectSelection,
  getToggledProperties,
  getToggleablePersistedPropertiesByType,
} from '../../common/objects';
import { dispatchAdded } from './objectActions';
import {
  getCurrentSlide,
  getSlidesCollection,
} from '../reducers/canvasReducer';
import smalltalk from '../../vendor/smalltalk';
import { saveStateToLocalStorage } from '../../common/localStorage';

const throttledDispatchViewportModified = throttle(
  (dispatch, params) => {
    dispatch({ type: 'VIEWPORT_MODIFIED', ...params });
  },
  250,
  { leading: false, trailing: true },
);

/**
 * (Alt-drag) selection
 *
 * @param selection
 */
export const copyMoveSelection = (selection, extraProps) => async (
  dispatch,
  getState,
  fabricCanvas,
) => {
  let isCopyEnabled = true;
  selection.forEachObject(o => {
    if (isFunction(o.isCopyEnabled) && !o.isCopyEnabled()) {
      isCopyEnabled = false;
    }
  });
  if (!isCopyEnabled) {
    return {};
  } else {
    const clonedSelection = await _duplicateActiveObject(
      selection,
      fabricCanvas,
      extraProps,
      true,
    );
    return {
      original: selection.getObjects(),
      duplicated: clonedSelection.getObjects(),
    };
  }
};

/**
 * (Alt-drag) single object
 *
 * @param object
 */
export const copyMoveObject = (object, extraProps) => async (
  dispatch,
  getState,
  fabricCanvas,
) => {
  if (isFunction(object.isCopyEnabled) && !object.isCopyEnabled()) {
    return {};
  } else {
    const left = object.left;
    const top = object.top;
    const clonedObject = await _duplicateObject(
      object,
      fabricCanvas,
      {
        left,
        top,
        ...extraProps,
      },
      true,
    );

    return { original: [object], duplicated: [clonedObject] };
  }
};

export const addObject = (
  type,
  overrides = {},
  beforeAddCallback = noop,
  afterAddCallback = noop,
) => (dispatch, getState, fabricCanvas) =>
  // eslint-disable-next-line no-async-promise-executor
  new Promise(async (resolve, reject) => {
    const defaultProperties = getState().propertiesReducer.objects[type]
      .properties;
    const propertiesFlags = getState().propertiesReducer.objects[type]
      .propertiesFlags;
    const toggleableProperties = getToggleablePersistedPropertiesByType(type);
    const cleanedOverrides = utils.cleanValuesDeep(overrides);
    const toggledDefaultProperties = getToggledProperties(
      defaultProperties,
      toggleableProperties,
      propertiesFlags,
    );

    const properties = {
      ...toggledDefaultProperties,
      ...cleanedOverrides,
      ludus: {
        ...defaultProperties.ludus,
        ...cleanedOverrides.ludus,
      },
    };

    try {
      dispatch({ type: 'OBJECT_INCREMENT_LOADING' });

      const object = await createObject(type, properties);

      const canvasSize = getState().contentReducer.presentation
        .canvasDimensions;
      configureObject(object, canvasSize);
      identifyObject(object);

      if (object.ludus?.format === 'svg') {
        object.set({ lockUniScaling: true });
        if (object.ludus.originalWidth < 300) {
          const scaleRatio = 300 / object.ludus.originalWidth;
          object.scaleX = scaleRatio;
          object.scaleY = scaleRatio;
        } else if (object.ludus.originalWidth > 1250) {
          const scaleRatio = 600 / object.ludus.originalWidth;
          object.scaleX = scaleRatio;
          object.scaleY = scaleRatio;
        }
        if (!object.width || !object.height) {
          object.width = object.ludus.originalWidth;
          object.height = object.ludus.originalHeight;
        }
      }

      // Set position property if not specified
      if (!('left' in properties) || !('top' in properties)) {
        let top;
        let left;
        if (viewport.isCanvasBiggerThanViewport(fabricCanvas, canvasSize)) {
          // Zoomed in, center object in viewport
          top = viewport.getVisualCenterInViewport(fabricCanvas).y;
          left = viewport.getVisualCenterInViewport(fabricCanvas).x;
        } else {
          // Zoomed out, center object on canvas
          top = canvasSize.height / 2;
          left = canvasSize.width / 2;
        }
        // Account for object origin

        object.setPositionByOrigin({ x: left, y: top }, 'center', 'center');
      }

      if (isFunction(beforeAddCallback)) {
        beforeAddCallback(object);
      }

      // Make sure new added image are not bigger than canvas by default
      if (
        type === 'image' &&
        object.ludus.limitedWidth &&
        object.ludus.limitedHeight &&
        object.ludus.format !== 'svg'
      ) {
        const documentSize = getState().presentationReducer.documentSize;
        const scaleX = documentSize.width / object.ludus.limitedWidth;
        const scaleY = documentSize.height / object.ludus.limitedHeight;
        const scale = Math.min(scaleX, scaleY);

        const width = object.ludus.limitedWidth * scale;
        const height = object.ludus.limitedHeight * scale;

        object.set({
          width: object.ludus.limitedWidth * scale,
          height: object.ludus.limitedHeight * scale,

          left: (documentSize.width - width) / 2,
          top: (documentSize.height - height) / 2,
        });
      }

      fabricCanvas.add(object);
      fabricCanvas.setActiveObject(object);
      if (isFunction(afterAddCallback)) {
        afterAddCallback(object);
      }

      fabricCanvas.isDrawingMode = false;

      dispatch({ type: 'OBJECT_DECREMENT_LOADING' });
      dispatchAdded(object)(dispatch, getState);
      dispatch({ type: 'DRAWING_MODE_DISABLED' });

      resolve(object);
    } catch (error) {
      console.error('Error adding object to canvas:', error);
      console.error('Type:', type);
      console.error('Properties:', properties);
      reject(error);
    }
  });

export const cleanCanvas = () => (dispatch, getState, fabricCanvas) => {
  // Copied from fabric.IText.prototype.exitEditingOnOthers
  fabricCanvas._objects.forEach(obj => {
    obj.selected = false;
    obj.dispose && obj.dispose();
    if (obj.isEditing) {
      obj.exitEditing();
    }
  });
};

export const resetViewport = containerRect => (
  dispatch,
  getState,
  fabricCanvas,
) => {
  const canvasSize = getState().contentReducer.presentation.canvasDimensions;
  const mode = getState().contentReducer.presentation.mode;
  if (mode === MODE_SLIDE) {
    viewport.updateViewport(fabricCanvas, canvasSize, {
      fitZoom: true,
      containerRect,
    });
  } else if (mode === MODE_SYMBOL) {
    // Always start Smart Block edition centered and fitted
    viewport.updateViewport(fabricCanvas, canvasSize, {
      fitZoom: true,
      containerRect,
    });
  } else if (mode === MODE_TEMPLATE) {
    // Always start template edition centered and fitted
    viewport.updateViewport(fabricCanvas, canvasSize, {
      fitZoom: true,
      containerRect,
    });
  }
};

export const resetActiveSelection = () => (
  dispatch,
  getState,
  fabricCanvas,
) => {
  const activeSelection = fabricCanvas.getActiveObject();
  if (activeSelection && isActiveSelection(activeSelection)) {
    fabricCanvas.discardActiveObject();
    const newSelection = new fabric.ActiveSelection(
      activeSelection.getObjects(),
      { canvas: fabricCanvas },
    );
    fabricCanvas.setActiveObject(newSelection).requestRenderAll();
  }
};

export const fitZoom = containerRect => (dispatch, getState, fabricCanvas) => {
  const canvasSize = getState().contentReducer.presentation.canvasDimensions;
  viewport.updateViewport(fabricCanvas, canvasSize, {
    fitZoom: true,
    containerRect,
  });
};

export const zoomByIncrement = type => (dispatch, getState, fabricCanvas) => {
  const canvasSize = getState().contentReducer.presentation.canvasDimensions;
  viewport.zoomByIncrement(fabricCanvas, canvasSize, type);
};

export const zoomByEvent = event => (dispatch, getState, fabricCanvas) => {
  const canvasSize = getState().contentReducer.presentation.canvasDimensions;
  viewport.zoomByEvent(fabricCanvas, canvasSize, event);
};

export const panByEvent = event => (dispatch, getState, fabricCanvas) => {
  const canvasSize = getState().contentReducer.presentation.canvasDimensions;
  viewport.panByEvent(fabricCanvas, canvasSize, event);
};

export const paste = objects => (dispatch, getState, fabricCanvas) => {
  fabric.util.enlivenObjects(objects, items => {
    // Remove ids on all pasted objects
    items.forEach(utils.removeLudusIds);
    items.forEach(identifyObjectTree);
    items.forEach(
      fixObjectsReferencesFromObjectTree(fabricCanvas.getObjects()),
    );
    const isEditingSymbol =
      getState().contentReducer.presentation.mode === MODE_SYMBOL;
    let pastingSymbolInSymbol = false;
    if (isEditingSymbol) {
      remove(items, o => {
        pastingSymbolInSymbol =
          o.type === 'ludusSymbol' ? true : pastingSymbolInSymbol;
        return o.type === 'ludusSymbol';
      });
    }
    if (items.length === 1 && !isActiveSelection(items[0])) {
      const object = items[0];
      fabricCanvas.add(object);
      fabricCanvas.setActiveObject(object);
      dispatchAdded(object)(dispatch, getState);
    } else if (items.length === 1 || isActiveSelection(items[0])) {
      const activeObjects = items[0]._objects;
      activeObjects.forEach(object => {
        const item = object;
        fabricCanvas.add(item);
        item.left += items[0].left + items[0].width / 2;
        item.top += items[0].top + items[0].height / 2;
      });
      fabricCanvas.discardActiveObject();
      fabricCanvas
        .setActiveObject(
          new fabric.ActiveSelection(activeObjects, { canvas: fabricCanvas }),
        )
        .requestRenderAll();
      dispatchAdded(activeObjects)(dispatch, getState);
    } else if (items.length > 1 || isActiveSelection(items[0])) {
      items.forEach(object => {
        fabricCanvas.add(object);
      });
      fabricCanvas.discardActiveObject();
      fabricCanvas
        .setActiveObject(
          new fabric.ActiveSelection(items, { canvas: fabricCanvas }),
        )
        .requestRenderAll();
      dispatchAdded(items)(dispatch, getState);
    } else if (items.length === 0 && pastingSymbolInSymbol) {
      const symbolError = {
        title: variables.errors.symbolInSymbol.title,
        message: variables.errors.symbolInSymbol.message,
      };
      throw symbolError;
    } else {
      throw new Error('not a fabric object');
    }
  });
};

const _duplicateObject = (
  originalObject,
  fabricCanvas,
  extraProps = {},
  preventSelection,
) =>
  new Promise(resolve => {
    originalObject.clone(clonedObject => {
      utils.removeLudusIds(clonedObject);
      identifyObjectTree(clonedObject);
      fixObjectsReferencesFromObjectTree(fabricCanvas.getObjects())(
        clonedObject,
      );
      clonedObject.set(extraProps);
      clonedObject.set('canvas', fabricCanvas);
      fabricCanvas.add(clonedObject);
      clonedObject.moveTo(fabricCanvas.getObjects().indexOf(originalObject));
      !preventSelection &&
        fabricCanvas.setActiveObject(clonedObject).requestRenderAll();
      resolve(clonedObject);
    }, variables.propsToSave);
  });

const _duplicateActiveObject = (
  selection,
  fabricCanvas,
  extraProps = {},
  preventSelection,
) =>
  new Promise(resolve => {
    selection.clone(clonedSelection => {
      utils.removeLudusIds(clonedSelection);
      identifyObjectTree(clonedSelection);
      fixObjectsReferencesFromObjectTree(fabricCanvas.getObjects())(
        clonedSelection,
      );
      clonedSelection.set(extraProps);
      clonedSelection.set('canvas', fabricCanvas);
      clonedSelection._restoreObjectsState();
      clonedSelection.forEachObject((clonedObject, index) => {
        const clonedIndex = fabricCanvas
          .getObjects()
          .indexOf(selection.item(index));
        fabricCanvas.add(clonedObject);
        fabricCanvas.moveTo(clonedObject, clonedIndex);
        utils.removeLudusIds(clonedObject);
      });

      !preventSelection &&
        fabricCanvas
          .setActiveObject(
            new fabric.ActiveSelection(clonedSelection._objects, {
              canvas: fabricCanvas,
            }),
          )
          .requestRenderAll();
      resolve(clonedSelection);
    }, variables.propsToSave);
  });

export const duplicate = (selection, lastMove) => async (
  dispatch,
  getState,
  fabricCanvas,
) => {
  if (fabricCanvas.editingSymbol) {
    return false;
  }
  const _x = lastMove ? lastMove.x : selection.width * selection.scaleX * 0.1;
  const _y = lastMove ? lastMove.y : selection.height * selection.scaleY * 0.1;
  if (isActiveSelection(selection)) {
    // search for max index in the selection.
    // All object will get positionned there once duplicated.
    let maxIndex = 0;
    selection.forEachObject(o => {
      const index = fabricCanvas.getObjects().indexOf(o);
      if (index >= maxIndex) {
        maxIndex = index + 1;
      }
    });
    const clonedSelection = await _duplicateActiveObject(
      selection,
      fabricCanvas,
      {
        left: selection.left + _x,
        top: selection.top + _y,
      },
    );
    dispatchAdded(clonedSelection)(dispatch, getState);
  } else {
    const object = selection;

    const clonedObject = await _duplicateObject(object, fabricCanvas, {
      left: object.left + _x,
      top: object.top + _y,
    });
    dispatchAdded(clonedObject)(dispatch, getState);
  }
};

/**
 * Alt drag
 *
 * @param object
 */
export const copyMove = (object, extraProps) => dispatch => {
  if (
    isActiveSelection(object) &&
    !isObjectOfType(object, 'iframe', 'path-group')
  ) {
    // ALT-MOVE to duplicate a selection
    return dispatch(copyMoveSelection(object, extraProps));
  }

  // ALT-MOVE to duplicate an object
  return dispatch(copyMoveObject(object, extraProps));
};

/**
 * Once duplicated by mouse, we get the last duplicated movement
 * to feed the alt-drag
 *
 * @param copyMoved
 */
export const getLastCopyMove = copyMoved => () => {
  // get the absolute translation of original and duplicated obj, whatever if group or not
  const originalMatrix = fabric.util.qrDecompose(
    copyMoved.original[0].calcTransformMatrix(),
  );
  const duplicatedMatrix = fabric.util.qrDecompose(
    copyMoved.duplicated[0].calcTransformMatrix(),
  );
  return {
    x: originalMatrix.translateX - duplicatedMatrix.translateX,
    y: originalMatrix.translateY - duplicatedMatrix.translateY,
  };
};
/**
 * We sometimes want to cancel alt drag (when alt was pressed but no movement was
 * detected)
 *
 * @param copyMoved
 */
export const cancelCopyMove = objects => (dispatch, getState, fabricCanvas) => {
  objects.forEach(duplicated => {
    fabricCanvas.remove(duplicated);
  });
};

export const addArrow = () => dispatch => {
  dispatch(addObject('arrow'));
};

export const addLine = () => dispatch => {
  dispatch(addObject('line'));
};

export const addCircle = (scaleX = 1, scaleY = 1, left, top) => dispatch => {
  const overrides = {
    scaleX,
    scaleY,
    left,
    top,
  };
  dispatch(addObject('circle', overrides));
};

export const addRectangle = (scaleX = 1, scaleY = 1, left, top) => dispatch => {
  const overrides = {
    scaleX,
    scaleY,
    left,
    top,
  };
  dispatch(addObject('rectangle', overrides));
};

export const addTitle = (
  text = variables.placeholders.title,
  left,
  top,
) => dispatch => {
  const overrides = { left, top, text };
  dispatch(
    addObject('title', overrides, null, object => {
      object.enterEditing();
      object.selectAll();
    }),
  );
};

export const addText = (
  text = variables.placeholders.paragraph,
) => dispatch => {
  const overrides = { text };
  dispatch(
    addObject('paragraph', overrides, null, object => {
      object.enterEditing();
      object.selectAll();
    }),
  );
};

export const addList = (
  type,
  text = variables.placeholders.bulletedList,
) => dispatch => {
  const overrides = {
    text,
    fontSize: 50,
    contents: {
      ops: [
        {
          insert: `${text}\n`,
          attributes: { list: type },
        },
      ],
    },
  };
  dispatch(
    addObject('paragraph', overrides, null, object => {
      object.enterEditing();
      object.selectAll();
    }),
  );
};

export const addRichMedia = overrides => dispatch => {
  const cleanedOverrides = omit(overrides, 'type');
  dispatch(addObject('rich', cleanedOverrides));
};

export const addLottie = data => dispatch => {
  const overrides = {
    src: data.url,
    ludus: {
      url: data.url,
    },
  };
  dispatch(addObject('lottie', overrides));
};

export const addVideo = data => dispatch => {
  const overrides = {
    src: data.thumbnail,
    ludus: pick(data, [
      'publicId',
      'format',
      'version',
      'source',
      'thumbnail',
      'url',
      'originalUrl',
      'originalWidth',
      'originalHeight',
      'limitedWidth',
      'limitedHeight',
      'backgroundColor',
      'textColor',
    ]),
  };
  overrides.ludus.uploadingDate = Date.now();
  dispatch(addObject('video', overrides));
};

export const addSVGFromString = string => dispatch => {
  // when pasting from illustrator we got a last character that bugs fabric.
  // we check the last chacater to be a closing tag
  const svgString =
    string.slice(-1) !== '>'
      ? string.slice(0, string.lastIndexOf('>') + 1)
      : string;
  const url = `data:image/svg+xml;utf8,${encodeURIComponent(svgString)}`;
  const overrides = {
    url: url,
    format: 'svg',
    ludus: {
      source: 'clipboard',
      format: 'svg',
    },
  };
  dispatch(addImage(overrides));
};

export const addImage = (data, targetId) => (
  dispatch,
  getState,
  fabricCanvas,
) => {
  const overrides = {
    src: data.url,
    editingSymbol: fabricCanvas.editingSymbol,
    ludus: pick(data, [
      'publicId',
      'format',
      'version',
      'source',
      'url',
      'size',
      'originalUrl',
      'originalWidth',
      'originalHeight',
      'limitedWidth',
      'limitedHeight',
      'backgroundColor',
      'textColor',
      'hasAlpha',
    ]),
  };
  overrides.ludus.uploadingDate = Date.now();
  // remove useless url if not a real one
  if (
    data.format === 'svg' &&
    ['iconscout', 'nounproject', 'brandfetch'].includes(data.source)
  ) {
    if (data.url.indexOf('data:image/') >= 0) {
      set(overrides, 'ludus.url', null);
    }
    dispatch({ type: 'IMAGE_UPLOAD_START' });
    fetch(data.url)
      .then(response => {
        if (response.status !== 200) {
          dispatch({
            type: 'IMAGE_UPLOAD_ERROR',
            publicId: targetId,
          });
          throw new Error(
            `Something is wrong with this file, error ${response.status}`,
          );
        }
        return response.blob();
      })
      .then(images => {
        // Then create a local URL for that image and print it
        dispatch(uploadFile(images, true, true)).then(fileData => {
          dispatch({ type: 'IMAGE_UPLOAD_COMPLETE' });
          dispatch(addImage(fileData));
        });
      })
      .catch(e => {
        smalltalk.alert('Sorry', `${e.message}`);
      });
  } else if (targetId) {
    // REPLACE IMAGE
    dispatch(overrideImage(targetId, overrides));
  } else {
    // Dispatch an upload start event if added via link or flip search menu
    if (data.source !== 'Cloudinary') {
      dispatch({ type: 'IMAGE_FETCHING_START' });
    }
    dispatch(addObject('image', overrides));
  }
};

export const addIframe = url => dispatch => {
  const overrides = {
    ludus: {
      url,
    },
  };
  dispatch(addObject('iframe', overrides));
};

/**
 * Add a symbol (smart object) to the canvas
 *
 * @param symbolData (as returned by our API)
 */
export const addSymbol = symbolData => dispatch => {
  const symbolContent = migrateJSON(JSON.parse(symbolData.content));
  // Migrate old symbols
  if (symbolData.version === 1) {
    dispatch(
      symbolActions.convertToSymbol(
        symbolData.name,
        symbolContent,
        symbolData.id,
      ),
    );
  } else {
    // This is not really necessary but it will ensure we fetch the latest version when adding
    fabric.LudusSymbol.register(symbolData.id, symbolData.name, symbolContent);
    const overrides = {
      reference: symbolData.id,
      width: symbolContent.width,
      height: symbolContent.height,
    };
    dispatch(addObject('symbol', overrides));
  }
};

export const selectAll = () => (dispatch, getState, fabricCanvas) => {
  const canvas = fabricCanvas;
  canvas.discardActiveObject();
  const items = canvas
    .getObjects()
    .filter(o => o.selectable && o.type !== 'ludusGuideline')
    .map(o => {
      if (o.selectable) {
        return o.set('active', true);
      }
      return false;
    });
  if (items.length > 1) {
    // Create a selection of all the objects present on stage
    canvas
      .setActiveObject(new fabric.ActiveSelection(items, { canvas }))
      .requestRenderAll();
  } else if (items.length === 1) {
    // Just select the only object on stage
    canvas.setActiveObject(items[0]).requestRenderAll();
  } else {
    // Do nothing if there is nothing on stage
  }
  canvas.isDrawingMode = false;
  dispatch({ type: 'DRAWING_MODE_DISABLED' });
};

export const unselectAll = () => (dispatch, getState, fabricCanvas) => {
  fabricCanvas.discardActiveObject().requestRenderAll();
  dispatch({ type: 'SELECTION_CLEARED' });
};

export const unlockAll = () => (dispatch, getState, fabricCanvas) => {
  const presentation = getState().contentReducer.presentation;
  const currentSlide = presentation.slides[presentation.currentSlideId];
  const hasTemplate = !!currentSlide.templateId;
  const lockObject = fabricCanvas.getObjects().filter(obj => {
    if (!hasTemplate && obj.ludus?.referenceId && !obj.editable) {
      // unlock wrongly unlinked object of a template
      return true;
    }

    return obj.ludus && obj.ludus.locked === true;
  });
  lockObject.forEach(obj => {
    const o = obj;
    o.set(variables.regularProperties);
    if (!o.ludus) o.ludus = {};
    o.ludus.locked = false;
  });
  if (lockObject.length > 1) {
    const unlockedSelection = new fabric.ActiveSelection(lockObject, {
      canvas: fabricCanvas,
    });
    fabricCanvas.setActiveObject(unlockedSelection).requestRenderAll();
    fabricCanvas.getActiveObject().setCoords();
    dispatch({ type: 'ALL_UNLOCKED', object: lockObject });
  } else if (lockObject.length === 1) {
    // if we set 1 active object in active selection, fabric messed up coord of obj.
    fabricCanvas.setActiveObject(lockObject[0]);
    fabricCanvas.requestRenderAll();
    dispatch({ type: 'ALL_UNLOCKED', object: lockObject[0] });
  }
};

export const activateDrawingMode = () => (dispatch, getState, fabricCanvas) => {
  const canvas = fabricCanvas;
  canvas.discardActiveObject().requestRenderAll();
  canvas.isDrawingMode = true;
  dispatch({ type: 'SELECTION_CLEARED' });
  dispatch({ type: 'DRAWING_MODE_ENABLED' });
};

export const deactivateDrawingMode = () => (
  dispatch,
  getState,
  fabricCanvas,
) => {
  const canvas = fabricCanvas;
  canvas.isDrawingMode = false;
  dispatch({ type: 'DRAWING_MODE_DISABLED' });
};

export const getDrawingOptions = () => (dispatch, getState, fabricCanvas) => ({
  ...fabricCanvas.freeDrawingBrush,
});

export const updateDrawingOptions = options => (
  dispatch,
  getState,
  fabricCanvas,
) => {
  const canvas = fabricCanvas;
  if (options.color) {
    canvas.freeDrawingBrush.color = options.color;
  }
  if (options.simulatePressure !== undefined)
    canvas.freeDrawingBrush.simulatePressure = options.simulatePressure;

  if (options.thinning) {
    canvas.freeDrawingBrush.thinning = options.thinning;
  }

  if (options.width) {
    canvas.freeDrawingBrush.width = options.width;
  }
  if (options.size) {
    canvas.freeDrawingBrush.size = options.size;
  }
  if (options.detectShapes === true || options.detectShapes === false) {
    canvas.detectDrawingShapes = options.detectShapes;
  }
};

export const updateBackgroundColor = color => (
  dispatch,
  getState,
  fabricCanvas,
) => {
  const canvas = fabricCanvas;
  canvas.backgroundColor = color;
  canvas.requestRenderAll();
};

export const showOverlay = (mode, params = {}) => dispatch => {
  dispatch({ type: 'SHOW_OVERLAY', mode, params });
};

export const hideOverlay = () => dispatch => {
  dispatch({ type: 'HIDE_OVERLAY' });
};

export const setFocus = id => dispatch => {
  dispatch({ type: 'FOCUS_IN', id });
};

export const unsetFocus = id => dispatch => {
  dispatch({ type: 'FOCUS_OUT', id });
};

export const setColor = (color, propName) => (dispatch, getState) => {
  const activeObject = getState().selectionReducer.activeObject;
  if (activeObject) {
    const selection = new ObjectSelection(activeObject);
    selection.setProperty(propName || 'fill', color);
    dispatch({ type: 'OBJECT_MODIFIED', object: activeObject });
  } else {
    dispatch(updateBackgroundColor(color));
    dispatch(dispatchBackgroundUpdated(color));
  }
};

export const forwardKeyDownToActiveSelection = key => (dispatch, getState) => {
  const objectSelection = new ObjectSelection(
    getState().selectionReducer.activeObject,
  );
  return objectSelection.triggerKeyDownWithKey(key);
};

export const forwardKeyUpToActiveSelection = key => (dispatch, getState) => {
  const objectSelection = new ObjectSelection(
    getState().selectionReducer.activeObject,
  );
  return objectSelection.triggerKeyUpWithKey(key);
};

export const dispatchUnlockAllSilent = () => dispatch => {
  dispatch({ type: 'ALL_UNLOCKED_SILENT' });
};

export const dispatchNewSlide = (templateId, tail = false) => (
  dispatch,
  getState,
  fabricCanvas,
) => {
  const { isLoading } = getState().loadingReducer;
  if (isLoading) {
    return;
  }

  let defaultSlideProperties;

  const themes = getState().themes;
  let template;

  if (templateId) {
    template = getTemplateById(themes.all, templateId);
  }

  if (template) {
    defaultSlideProperties = {
      ...template.content,
      templateId: template.id,
      templateVersion: template.hash || template.version,
    };
  } else {
    defaultSlideProperties = getState().propertiesReducer.slide;
  }

  fabricCanvas.clear();
  fabricCanvas.setBackgroundColor(defaultSlideProperties.background);
  fabricCanvas.requestRenderAll();
  const id = uuid.v4();
  dispatch({
    type: 'NEW_SLIDE',
    id,
    tail,
    ...defaultSlideProperties,
  });
  dispatch(generateSlideThumbnail(id));
};

export const addNewSlide = (
  skipChoice = false,
  tail = false,
  vAlign,
  hStickTo,
) => (dispatch, getState) => {
  const { presentation } = getState().contentReducer;
  if (skipChoice || presentation.mode === MODE_TEMPLATE) {
    // Find most frequent used theme in the presentation and use the blank template
    const slides = getSlidesCollection(getState().contentReducer.presentation);
    const themes = getState().themes.all;
    const { id } = getCommonThemeForSlides(themes, slides);
    const template = getBlankTemplateByThemeId(themes, id);
    const templateId = template ? template.id : null;
    dispatch(dispatchNewSlide(templateId, tail));
  } else {
    const currentSlide = getCurrentSlide(
      getState().contentReducer.presentation,
    );
    dispatch(
      showOverlay('themes', {
        action: 'new',
        currentTemplateId: currentSlide.templateId,
        tail,
        vAlign,
        hStickTo,
      }),
    );
  }
};

export const dispatchPreviousSlide = () => dispatch => {
  dispatch(cleanCanvas());
  dispatch({ type: 'PREVIOUS_SLIDE' });
};

export const dispatchNextSlide = () => dispatch => {
  dispatch(cleanCanvas());
  dispatch({ type: 'NEXT_SLIDE' });
};

export const dispatchGoToSlide = index => (dispatch, getState) => {
  dispatch(cleanCanvas());
  dispatch({ type: 'GO_TO_SLIDE', index });
};

export const dispatchPasteSlides = (slides, index, newId) => dispatch => {
  dispatch({
    type: 'PASTE_SLIDES',
    slides,
    index,
    newId,
  });
  forEach(slides, slide => {
    dispatch(generateSlideThumbnail(slide.id));
  });

  dispatch(dispatchGoToSlide(index));
};

export const dispatchDuplicateSlides = (idTuples, index, newId) => dispatch => {
  dispatch({
    type: 'DUPLICATE_SLIDES',
    idTuples,
    index,
    newId,
  });
};

export const dispatchReorder = (ids, newId) => (dispatch, getState) => {
  const orderChanged =
    JSON.stringify(ids) !==
    JSON.stringify(getState().contentReducer.presentation.order);
  if (orderChanged) {
    dispatch({ type: 'REORDER', ids, newId });
  }
};

export const dispatchOpenQuickAddMenu = () => dispatch => {
  dispatch({ type: 'QUICK_ADD_MENU_OPEN' });
};

export const dispatchCloseQuickAddMenu = () => dispatch => {
  dispatch({ type: 'QUICK_ADD_MENU_CLOSE' });
};

export const dispatchQuickAddMenuFlip = () => dispatch => {
  dispatch({ type: 'QUICK_ADD_MENU_FLIP' });
};

export const dispatchQuickAddMenuUnflip = () => dispatch => {
  dispatch({ type: 'QUICK_ADD_MENU_UNFLIP' });
};

export const dispatchQuickAddMenuFlipComplete = flipped => dispatch => {
  dispatch({ type: 'QUICK_ADD_MENU_FLIP_COMPLETE', flipped });
};

export const dispatchQuickAddMenuSearchResults = results => dispatch => {
  dispatch({ type: 'QUICK_ADD_MENU_SEARCH_RESULTS', results });
};

export const dispatchQuickAddMenuBackSearchResults = (
  source,
  query,
  results,
) => dispatch => {
  dispatch({
    type: 'QUICK_ADD_MENU_BACK_SEARCH_RESULTS',
    source,
    query,
    results,
  });
};

export const dispatchDragStart = () => dispatch => {
  dispatch({ type: 'DRAG_START' });
};

export const dispatchDragEnd = () => dispatch => {
  dispatch({ type: 'DRAG_END' });
};

export const dispatchOpenPopup = () => dispatch => {
  dispatch({ type: 'OPEN_POPUP' });
};

export const dispatchClosePopup = () => dispatch => {
  dispatch({ type: 'CLOSE_POPUP' });
};

export const dispatchIncrementLoading = () => dispatch => {
  dispatch({ type: 'INCREMENT_LOADING' });
};

export const dispatchDecrementLoading = () => dispatch => {
  dispatch({ type: 'DECREMENT_LOADING' });
};

export const dispatchChangeTitle = title => ({ type: 'TITLE_CHANGED', title });

export const dispatchChangeDescription = description => ({
  type: 'DESCRIPTION_CHANGED',
  description,
});

export const dispatchChangeSlug = slug => ({ type: 'SLUG_CHANGED', slug });

export const dispatchChangeViewerSettings = settings => ({
  type: 'VIEWER_SETTINGS_CHANGED',
  ...settings,
});

export const dispatchChangeGoogleDriveId = googleDriveId => ({
  type: 'GOOGLE_DRIVE_ID_CHANGED',
  googleDriveId,
});

export const dispatchChangePassword = password => ({
  type: 'PASSWORD_CHANGED',
  password,
});

export const dispatchAsyncRendered = object => dispatch => {
  dispatch({ type: 'OBJECT_ASYNC_RENDERED', object });
};

export const dispatchAllImagesRendered = object => dispatch => {
  dispatch({ type: 'OBJECT_ALL_IMAGES_RENDERED', object });
};

export const dispatchBackgroundUpdated = background => dispatch => {
  dispatch({ type: 'BACKGROUND_UPDATED', background });
};

export const dispatchSlideTransitionUpdated = transition => dispatch => {
  dispatch({ type: 'SLIDE_TRANSITION_UPDATED', transition });
};

export const dispatchHelp = message => dispatch => {
  dispatch({ type: 'HELP', message });
};

export const toggleCollaboratorsSelection = () => (
  dispatch,
  getState,
  fabricCanvas,
) => {
  const visible = !fabricCanvas.showCollaboratorsSelection;
  fabricCanvas.showCollaboratorsSelection = visible;
  fabricCanvas.requestRenderAll();
  dispatch({ type: 'COLLABORATORS_SELECTION_MODIFIED', visible });
};

export const toggleRulers = () => (dispatch, getState, fabricCanvas) => {
  const visible = !fabricCanvas.showRulers;
  fabricCanvas.showRulers = visible;
  fabricCanvas.requestRenderAll();
  saveStateToLocalStorage('editor', { rulersReducer: { showRulers: visible } });
  dispatch({ type: 'RULERS_MODIFIED', visible });
};

export const dispatchViewportModified = () => (
  dispatch,
  getState,
  fabricCanvas,
) => {
  throttledDispatchViewportModified(dispatch, {
    mode: getState().contentReducer.presentation.mode,
    zoomLevel: fabricCanvas.getZoom(),
    center: fabricCanvas.getViewportCenter(),
  });
};

export const dispatchSelectionRestored = object => dispatch => {
  dispatch({ type: 'SELECTION_RESTORED', object: object });
};
